import { useEffect, useState } from 'react';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cliengoQueries } from '@cliengo/mfe-utils/hooks';
import { Breadcrumbs, useTheme } from '@mui/material';

import { BreadcrumbItem, createCrumbs } from './crumbs-engine';

import { useTranslate } from '@/hooks';

export const BreadCrumbs = () => {
  const {
    data: websites,
    status: websitesStatus,
    refetch: refetchWebsites,
  } = cliengoQueries.websites();
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const location = useLocation();
  const { t } = useTranslate('crumbs');
  const navigate = useNavigate();

  const crumbs = createCrumbs([
    {
      path: '/',
      label: t('home'),
      children: [
        {
          label: t('myChatbots'),
          path: '/chatbot',
          children: [
            {
              path: ':websiteId',
              slot: (websiteId: string) => {
                const website = websites?.find((w) => w.id === websiteId);

                return {
                  label: website?.title || t('untitledChatbot'),
                  id: website?.id,
                };
              },
              children: [
                {
                  path: '/conversation',
                  label: t('conversation'),
                },
                {
                  path: '/personality',
                  label: t('personality'),
                },
                {
                  path: '/training-data',
                  label: t('training'),
                  children: [
                    {
                      path: '/about-business',
                      label: t('aboutBusiness'),
                    },
                    {
                      path: '/advanced',
                      label: t('advanced'),
                    },
                  ],
                },
                {
                  path: '/appearance',
                  label: t('appearance'),
                },
                {
                  path: '/integrations',
                  label: t('integrations'),
                },
                {
                  path: '/install',
                  label: t('connections'),
                  children: [
                    {
                      path: '/whatsapp-lite',
                      label: 'WhatsApp Lite',
                    },
                    {
                      path: '/whatsapp-form',
                      label: 'WhatsApp Form',
                    },
                    {
                      path: '/web',
                      label: 'Widget Web',
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          label: t('contacts'),
          path: '/contacts',
          children: [
            {
              label: t('management'),
              path: '/management',
            },
            {
              label: t('autoasignation'),
              path: '/autoasignation',
            },
            {
              label: t('statuses'),
              path: '/statuses',
            },
            {
              label: t('update'),
              path: '/update',
            },
            {
              label: t('autoresponses'),
              path: 'autoresponses/email',
            },
          ],
        },
        {
          label: 'Inbox',
          path: '/live',
        },
        {
          label: t('team'),
          path: '/team',
        },
        {
          label: t('campaigns'),
          path: '/campaigns',
          children: [
            {
              label: 'HSM',
              path: '/hsm',
            },
            {
              label: 'Google Ads',
              path: '/:companyId',
            },
          ],
        },
        {
          label: t('reports'),
          path: '/reports',
          children: [
            {
              label: t('reportsChatbot'),
              path: '/reports-chatbot',
            },
            {
              label: t('reportsAi'),
              path: '/reports-ai',
            },
          ],
        },
        {
          label: t('affiliates'),
          path: '/affiliates',
        },
      ],
    },
  ]);

  function ensureCorrectLabelSize(label: string) {
    const length = label.length;

    if (length > 40) {
      const firstHalf = label.substring(0, 10);
      const secondHalf = label.substring(length - 5, length);

      return firstHalf + '...' + secondHalf;
    }

    return label;
  }

  useEffect(() => {
    const handleRefresh = () => {
      refetchWebsites().catch(console.error);
    };

    window.addEventListener('refreshWebsites', handleRefresh);
    return () => {
      window.removeEventListener('refreshWebsites', handleRefresh);
    };
  }, [refetchWebsites]);

  useEffect(() => {
    const newCrumbs = crumbs.get(location.pathname);

    setBreadcrumbs(newCrumbs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, websites]);

  // PATCH: Redirect if 'undefined' is in the chatbot path
  useEffect(() => {
    if (
      location.pathname.includes('/chatbot/') &&
      location.pathname.includes('/undefined')
    ) {
      const segments = location.pathname.split('/');
      const undefinedIndex = segments.indexOf('undefined');

      // Check if 'undefined' is a path segment after 'chatbot'
      if (undefinedIndex > -1) {
        const chatbotIndex = segments.indexOf('chatbot');

        if (chatbotIndex > -1 && undefinedIndex > chatbotIndex) {
          navigate('/chatbot');
        }
      }
    }
  }, [location.pathname, navigate]);

  const theme = useTheme();

  if (websitesStatus === 'pending') {
    return null;
  }

  return (
    <Breadcrumbs
      aria-label="breadcrumb"
      separator={<NavigateNextIcon fontSize="small" />}
      sx={{
        a: {
          textDecoration: 'none',
          color: theme.palette.text.primary,
        },
        '[data-active="true"]': {
          color: theme.palette.primary.main,
        },
      }}
    >
      {breadcrumbs.map((crumb, i) => (
        <Link
          data-active={i === breadcrumbs.length - 1}
          id={crumb.path}
          key={`crumb-${i}`}
          to={crumb.path}
        >
          {ensureCorrectLabelSize(crumb.label)}
        </Link>
      ))}
    </Breadcrumbs>
  );
};
